/**
 * Typed search object for the `/onboarding` route.
 *
 * Coerce every value, never type-guard it. TanStack's default search
 * parser runs JSON.parse on each raw value, so an all-digit id arrives
 * as a number. See `searchParams.ts` for the full rule.
 *
 * `step` is optional: an absent or unrecognized value means "resume from
 * the server's onboarding step", not a default of step 1. The route reads
 * an absent step as an instruction to resolve the fan's furthest reached
 * step from the server rather than pinning them to `username`.
 */
import {
  findStepBySlug,
  type typeOnboardingStepSlug,
} from "../features/onboarding/onboardingSteps";
import { coerceOptionalEnum, coerceOptionalString } from "./searchParams";

const EMAIL_STEPS = ["add-email", "code"] as const;

export type typeOnboardingSearch = {
  step?: typeOnboardingStepSlug | undefined;
  redirect?: string | undefined;
  /** The sign-in-methods step's own email sub-flow. Carried in the URL so a
   *  fan who refreshes mid-code does not lose their place, per url-state.mdc. */
  emailStep?: "add-email" | "code" | undefined;
  requestId?: string | undefined;
};

export function validateOnboardingSearch(
  search: Record<string, unknown>,
): typeOnboardingSearch {
  const raw = coerceOptionalString(search.step);
  const found = raw === undefined ? null : findStepBySlug(raw);
  const redirect = coerceOptionalString(search.redirect);
  const emailStep = coerceOptionalEnum(search.emailStep, EMAIL_STEPS);
  const requestId = coerceOptionalString(search.requestId);
  return {
    ...(found === null ? {} : { step: found.slug }),
    ...(redirect === undefined ? {} : { redirect }),
    ...(emailStep === undefined ? {} : { emailStep }),
    ...(requestId === undefined ? {} : { requestId }),
  };
}
